import React, { FC, Fragment, ChangeEvent, useState, useEffect } from 'react';
import { Alert, Box, FormControl, Link, Paper, TextField } from '@mui/material';
import { LabClient } from 'models/client-directory';
import { MaterialPriceList } from 'models/material';
import { customerSettings } from 'utils/customerSettings';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { CONSTANTS } from 'config/constants';
import { useTranslation } from 'react-i18next';
import MaterialListComponent from './material-list/material-list.component';
import MaterialListPageActions from './material-list-page-actions/material-list-page-actions.component';
import { Link as RouterLink } from 'react-router-dom';

type AssociatedMaterialProps = {
    labClient: LabClient;
    materialPriceLists: MaterialPriceList[];
    materialPriceListForClient: MaterialPriceList;
    savedPriceList: MaterialPriceList;
    isUsingDefault: boolean;
    onMaterialListChange: (materialList: MaterialPriceList) => void;
    onUseDefault: () => void;
};

const AssociatedMaterial: FC<AssociatedMaterialProps> = ({
    labClient,
    materialPriceLists,
    materialPriceListForClient,
    savedPriceList,
    isUsingDefault,
    onMaterialListChange,
    onUseDefault,
}: AssociatedMaterialProps) => {
    const { t: translate } = useTranslation(['common', 'client-directory']);
    const currentOrganization = currentOrgVar();
    const [materialPriceList, setMaterialPriceList] = useState<MaterialPriceList>({} as MaterialPriceList);
    const [freetextFilter, setFreetextFilter] = useState<string>('');
    const currency = customerSettings.getCustomerSetting(
        currentOrganization.customerSettings,
        CONSTANTS.SETTINGS_NAMES.CURRENCY,
    );

    useEffect(() => {
        if (materialPriceListForClient) {
            setMaterialPriceList(materialPriceListForClient);
        }
    }, [materialPriceListForClient, setMaterialPriceList]);

    const handleSelectPriceList = (materialList: MaterialPriceList) => {
        // The dialog returns the base list (no client overrides). When the user
        // re-selects the list they currently have saved, use the saved/merged
        // version so their client prices are preserved.
        const listToApply =
            savedPriceList?._id && materialList._id === savedPriceList._id ? savedPriceList : materialList;
        setMaterialPriceList(listToApply);
        onMaterialListChange(listToApply);
    };

    const handleGeneralDiscountChange = (generalDiscount: string) => {
        if (parseInt(generalDiscount) > 100) generalDiscount = '100';
        const mPriceList = { ...materialPriceList, generalDiscount: generalDiscount };
        setMaterialPriceList(mPriceList);
        onMaterialListChange(mPriceList);
    };

    const handleFreetextFilter = (event: ChangeEvent<HTMLInputElement>) => {
        setFreetextFilter(event.target.value);
    };

    const handleDiscountedPriceChange = (materialDiscountedPrice: { id?: string; discountedPrice: string }) => {
        const clonedMaterials = [...(materialPriceList.materials || [])];
        const mPriceList = {
            ...materialPriceList,
            materials: clonedMaterials.map((material) =>
                material._id === materialDiscountedPrice.id
                    ? { ...material, discountedPrice: parseInt(materialDiscountedPrice.discountedPrice) }
                    : material,
            ),
        };
        setMaterialPriceList(mPriceList);
        onMaterialListChange(mPriceList);
    };

    return (
        <Fragment>
            {!labClient.customer.parentClinicId ? (
                <Fragment>
                    <MaterialListPageActions
                        materialPriceList={materialPriceList}
                        isUsingDefault={isUsingDefault}
                        onSelectPriceList={handleSelectPriceList}
                        onUseDefault={onUseDefault}
                        onGeneralDiscountChange={handleGeneralDiscountChange}
                        materialPriceLists={materialPriceLists}
                        defaultMaterialPriceListId={currentOrganization.defaultMaterialPriceListId}
                    />
                    {!isUsingDefault && Object.keys(materialPriceList).length !== 0 && (
                        <Fragment>
                            <Box>
                                <FormControl margin="dense" fullWidth>
                                    <TextField
                                        id="free-text-filter"
                                        label={translate('client-directory:MATERIAL_LIST_FILTER_LABEL')}
                                        onChange={handleFreetextFilter}
                                    />
                                </FormControl>
                            </Box>
                            <Box mt={2}>
                                <MaterialListComponent
                                    materialPriceList={materialPriceList}
                                    onDiscountedPriceChange={handleDiscountedPriceChange}
                                    freetextFilter={freetextFilter}
                                    currency={materialPriceList.currency || currency}
                                />
                            </Box>
                        </Fragment>
                    )}
                </Fragment>
            ) : (
                <Paper variant="outlined">
                    <Box p={2}>
                        <Alert severity="info">
                            {translate('client-directory:DISABLED_DUE_TO_PARENT')}
                            &nbsp;
                            {labClient?.customer?.parentClinicId && (
                                <Link
                                    component={RouterLink}
                                    to={`/client-directory/clients/${labClient?.customer?.parentClinicId}/price-list#material-list`}
                                >
                                    {translate('common:SHOW_CLINIC')}
                                </Link>
                            )}
                        </Alert>
                    </Box>
                </Paper>
            )}
        </Fragment>
    );
};

export default AssociatedMaterial;
